import {
  Archive20Regular,
  Delete20Regular,
  Edit20Regular,
  Folder20Regular,
  Mail20Regular,
  MailArrowUp20Regular,
  Prohibited20Regular,
  Send20Regular,
} from '@fluentui/react-icons';
import { findIndex, get, sortBy } from 'lodash';
import React, { KeyboardEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Menu, MenuItem, MenuList, MenuPopover, MenuTrigger } from '../Menu';
import { FolderItem } from './Email.interface';
import './Email.scss';
import {
  MailFolderTreeItem,
  transformMailFolderListToTree,
} from './Utils/transformMailFolderListToTree';

export interface EmailFoldersListProps {
  folders: FolderItem[];
  folderSelected: (folder: FolderItem) => void;
  selectedFolder: FolderItem | null;
  onDeleteFolder?: (folder: FolderItem) => void;
}

const iconClass = 'sidebarIcons';

const wellknownFolderNames: Record<string, JSX.Element> = {
  inbox: <Mail20Regular className={iconClass} />,
  outbox: <MailArrowUp20Regular className={iconClass} />,
  sentitems: <Send20Regular className={iconClass} />,
  drafts: <Edit20Regular className={iconClass} />,
  deleteditems: <Delete20Regular className={iconClass} />,
  junkemail: <Prohibited20Regular className={iconClass} />,
  archive: <Archive20Regular className={iconClass} />,
};

/**
 * Sorts a list of folder, so that most common used folders
 * (like inbox) are listed first. All other folders are sorted alphabetically.
 */
const sortFolders = (folders: FolderItem[]) => {
  const foldersCopy = [...folders];
  const sorted: FolderItem[] = [];

  for (const wellKnownName of Object.keys(wellknownFolderNames)) {
    const tmp = findIndex(foldersCopy, { wellKnownName });
    if (tmp !== -1) {
      sorted.push(foldersCopy.splice(tmp, 1)[0]);
    }
  }

  return [...sorted, ...sortBy(foldersCopy, ['displayName'])];
};

const EmailFoldersList: React.FC<EmailFoldersListProps> = ({
  folders,
  folderSelected,
  selectedFolder,
  onDeleteFolder,
}) => {
  const { t } = useTranslation();

  const getFolderById = (id: string) => folders.find((folder) => id === folder.id);

  const getFolderIcon = (folder: FolderItem) =>
    get(
      wellknownFolderNames,
      get(folder, 'wellKnownName'),
      <Folder20Regular className={iconClass} />,
    ) as JSX.Element;

  const renderFolder = (node: MailFolderTreeItem, level: number) => {
    const folder = getFolderById(node.id);
    if (!folder) {
      return null;
    }

    const isSelected = folder.id === selectedFolder?.id;
    const unreadItemCount = Number(get(folder, 'unreadItemCount', 0));

    const rowClasses = ['folder'];
    if (isSelected) {
      rowClasses.push('selectedFolder');
    }

    const labelClasses = ['folderLabel'];
    if (unreadItemCount > 0) {
      labelClasses.push('folderContainsUnreadMails');
    }

    // Build accessible label for the folder
    const ariaLabelParts: string[] = [folder.displayName ?? ''];

    if (unreadItemCount > 0) {
      ariaLabelParts.push(
        t('email_folder_unread_count', {
          defaultValue: '{{count}} ungelesene Nachrichten',
          count: unreadItemCount,
        }),
      );
    } else if (
      folder.wellKnownName === 'drafts' &&
      folder.totalItemCount !== undefined &&
      folder.totalItemCount !== null
    ) {
      ariaLabelParts.push(
        t('email_folder_draft_count', {
          defaultValue: '{{count}} Entwürfe',
          count: folder.totalItemCount as number,
        }),
      );
    }

    ariaLabelParts.push(
      t('email_folders_list_item_aria_label', {
        defaultValue: 'Filtert die E-Mails auf den ausgewählten Ordner: {{folderName}}',
        folderName: folder.displayName,
      }),
    );

    const handleActivate = () => folderSelected(folder);
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleActivate();
      }
    };

    const folderRow: ReactElement = (
      <div
        role="treeitem"
        aria-selected={isSelected}
        aria-level={level}
        tabIndex={0}
        className={rowClasses.join(' ')}
        data-testid="folderLabel"
        aria-label={ariaLabelParts.join(', ')}
        onClick={handleActivate}
        onKeyDown={handleKeyDown}
        style={{ '--folder-level': level - 1 } as React.CSSProperties}
      >
        {getFolderIcon(folder)}
        <span className={labelClasses.join(' ')}>{folder.displayName}</span>
        {unreadItemCount > 0 && (
          <span className="folderBadge" aria-hidden="true">
            {folder.unreadItemCount}
          </span>
        )}
        {folder.wellKnownName === 'drafts' && (
          <span className="folderBadgeDrafts" aria-hidden="true">
            {folder.totalItemCount}
          </span>
        )}
      </div>
    );

    return (
      <li key={folder.id} role="none">
        {onDeleteFolder === undefined ? (
          folderRow
        ) : (
          <Menu openOnContext>
            <MenuTrigger disableButtonEnhancement>{folderRow}</MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem
                  icon={<Delete20Regular />}
                  disabled={Boolean(folder.wellKnownName)}
                  data-testid="deleteFolderMenuItem"
                  onClick={() => onDeleteFolder(folder)}
                >
                  {t('email_delete_folder', { defaultValue: 'Ordner löschen' })}
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        )}
        {node.items.length > 0 && (
          <ul role="group" className="treeGroup">
            {node.items.map((child) => renderFolder(child, level + 1))}
          </ul>
        )}
      </li>
    );
  };

  const items = transformMailFolderListToTree(sortFolders(folders));

  return (
    <div className="emailFoldersList" data-testid="emailFoldersList">
      <ul
        role="tree"
        className="treeRoot"
        aria-label={t('email_folders_root_aria_label', {
          defaultValue: 'Alle E-Mail Ordner',
        })}
      >
        {items.map((item) => renderFolder(item, 1))}
      </ul>
    </div>
  );
};

export default EmailFoldersList;
